import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { OTPInput, SlotProps } from 'input-otp'
import { useCallback, useState } from 'react'
import { z } from 'zod'
import { LinkButton } from '~/features/navigation'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import { Alert, Icon, cn } from '@hap/components'

export const Route = createFileRoute('/_public/signup/code')({
  validateSearch: z.object({ email: z.string() }),
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const mutator = useMutation(trpc.signup.verifyCode.mutationOptions())
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async (code: string) => {
    await mutator
      .mutateAsync({ email: search.email, code })
      .then((response) => {
        navigate({
          to: '/signup/organization',
          search: { token: response.signupToken, email: search.email },
        })
      })
      .catch((mutationError) => {
        setError(mutationError.message || 'An error occurred while verifying the code.')
      })
  }, [])

  return (
    <PublicTemplate width={640}>
      <section className="bg-accent grid gap-8 p-20">
        <header className="mx-auto mb-8 max-w-[440px] space-y-4 text-center">
          <h1 className="text-4xl font-bold">We emailed you a signup code</h1>
          <p className="text-xl">
            We sent an email to <strong className="font-semibold">{search.email}</strong>.
            If you don’t see the email, check your spam or junk folder.
          </p>
        </header>

        <div className="grid gap-2">
          <div>
            <OTPInput
              maxLength={6}
              onComplete={handleSubmit}
              render={({ slots }) => (
                <div className="grid grid-cols-6 items-center gap-0">
                  {slots.map((slot, idx) => (
                    <Slot key={idx} {...slot} />
                  ))}
                </div>
              )}
            />
          </div>

          {error && (
            <Alert variant="warn" className="mt-4">
              {error}
            </Alert>
          )}

          <div className="text-accent-foreground relative my-10 flex items-center border-t text-center">
            <span className="bg-accent absolute left-1/2 top-1/2 inline-block -translate-x-1/2 -translate-y-1/2 p-2">
              Or
            </span>
          </div>

          <div className="grid">
            <LinkButton to="/signup" variant="secondary">
              <Icon name="arrow-left" /> &nbsp; Back to sign up options
            </LinkButton>
          </div>
        </div>
      </section>
    </PublicTemplate>
  )
}

function Slot(props: SlotProps) {
  return (
    <div
      className={cn(
        'h-30 relative font-mono text-[4rem]',
        'flex items-center justify-center',
        'transition-all duration-300',
        'border-input-secondary-background border-y border-r first:rounded-l-md first:border-l last:rounded-r-md',
        'group-hover:border-input-foreground/20 group-focus-within:border-input-background',
        'outline-hidden outline-0',
        {
          'bg-input-background border-input-focus-border outline-hidden z-10 shadow-[0px_0px_0px_3px_hsl(var(--input-focus-shadow))]':
            props.isActive,
        },
      )}
    >
      <div className="group-has-[input[data-input-otp-placeholder-shown]]:opacity-20">
        {props.char ?? props.placeholderChar}
      </div>
      {props.hasFakeCaret && <FakeCaret />}
    </div>
  )
}

function FakeCaret() {
  return (
    <div className="animate-caret-blink pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="h-8 w-px bg-white" />
    </div>
  )
}
